import React, { useMemo, useState } from 'react';
import axios from 'axios';
import config from './config';

const ContactUs: React.FC = () => {
  const contactEmail = 'support@exammasters.com.au';

  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [subject, setSubject] = useState('');
  const [message, setMessage] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [submitMessage, setSubmitMessage] = useState<string | null>(null);
  const [submitError, setSubmitError] = useState<string | null>(null);

  const apiBaseUrl = `${config.apiBaseUrl}/api`;

  const isFormValid = useMemo(() => {
    return name.trim().length > 0 && email.trim().length > 0 && subject.trim().length > 0 && message.trim().length > 0;
  }, [name, email, subject, message]);

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    if (!isFormValid || submitting) {
      return;
    }

    try {
      setSubmitting(true);
      setSubmitMessage(null);
      setSubmitError(null);

      await axios.post(`${apiBaseUrl}/contact/support`, {
        name,
        email,
        subject,
        message,
      });

      setSubmitMessage('Message sent successfully. Our support team will reach out soon.');
      setName('');
      setEmail('');
      setSubject('');
      setMessage('');
    } catch (error: any) {
      const statusCode = error?.response?.status;
      if (statusCode === 429) {
        setSubmitError('Too many requests. Please wait a few minutes before trying again.');
      } else {
        setSubmitError('Failed to send message. Please try again later.');
      }
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div className="min-h-[60vh] bg-gray-50 p-4 md:p-6">
      <div className="max-w-4xl mx-auto">
        <div className="mb-5">
          <h1 className="text-2xl md:text-3xl font-bold text-gray-900">Contact Us</h1>
          <p className="mt-2 text-sm md:text-base text-gray-600">
            Have a question about courses, billing, or your account? Send us a message.
          </p>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 md:gap-6">
          <div className="lg:col-span-2 bg-white border border-gray-200 rounded-lg shadow-sm p-4 md:p-6">
            <form onSubmit={handleSubmit} className="space-y-4">
              <div>
                <label className="block text-left text-sm font-semibold text-gray-700 mb-1">Name</label>
                <input
                  type="text"
                  value={name}
                  onChange={(event) => setName(event.target.value)}
                  maxLength={80}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Your name"
                />
              </div>

              <div>
                <label className="block text-left text-sm font-semibold text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  onChange={(event) => setEmail(event.target.value)}
                  maxLength={120}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="you@example.com"
                />
              </div>

              <div>
                <label className="block text-left text-sm font-semibold text-gray-700 mb-1">Subject</label>
                <input
                  type="text"
                  value={subject}
                  onChange={(event) => setSubject(event.target.value)}
                  maxLength={120}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="How can we help?"
                />
              </div>

              <div>
                <label className="block text-left text-sm font-semibold text-gray-700 mb-1">Message</label>
                <textarea
                  value={message}
                  onChange={(event) => setMessage(event.target.value)}
                  maxLength={3000}
                  rows={6}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500"
                  placeholder="Write your message..."
                />
              </div>

              {submitMessage && (
                <div className="p-3 rounded-lg border border-green-200 bg-green-50 text-green-800 text-sm">
                  {submitMessage}
                </div>
              )}

              {submitError && (
                <div className="p-3 rounded-lg border border-red-200 bg-red-50 text-red-700 text-sm">
                  {submitError}
                </div>
              )}

              <button
                type="submit"
                disabled={!isFormValid || submitting}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg font-semibold hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
              >
                {submitting ? 'Sending...' : 'Send Message'}
              </button>
            </form>
          </div>

          <div className="bg-white border border-gray-200 rounded-lg shadow-sm p-4 md:p-6">
            <h2 className="text-lg font-semibold text-gray-900">Support</h2>
            <p className="mt-2 text-sm text-gray-600">You can also email us directly.</p>
            <a
              href={`mailto:${contactEmail}`}
              className="inline-block mt-3 text-blue-600 hover:text-blue-700 font-semibold"
            >
              {contactEmail}
            </a>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ContactUs;
